import type { ChangeEventHandler } from 'react';
import type { PageChangeFn } from '../../../components/Pagination/Pagination';
import type { ResultsPerPageChangeFn } from '../../../components/Pagination/ResultsPerPageDropdown';
import type { Media } from '../../../lib/flotiq-client/types/api-response';

import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { saveUnsplashMedia } from '../../../lib/flotiq-client';
import {
  parseResponseErrors,
  ResponseError,
} from '../../../lib/flotiq-client/response-errors';
import { EMPTY_FUNCTION } from '../../../lib/globals';
import { getTestProps } from '../../../lib/helpers';
import { newMediaStore } from '../../../lib/store/newMediaStore';

import Dropdown from '../../../components/Dropdown/Dropdown';
import Heading from '../../../components/Heading/Heading';
import Input from '../../../components/Input/Input';
import MediaGrid from '../../../components/MediaGrid/MediaGrid';
import UnsplashCard from '../../../components/UnsplashCard/UnsplashCard';

import { useUnsplashMedia } from '../../../hooks/api';
import useApiErrorsToast from '../../../hooks/api/useApiErrorsToast';
import useSelectedSpace from '../../../hooks/useSelectedSpace';
import useToken from '../../../hooks/useToken';

interface UnsplashMediaProps {
  /**
   * Limit of an element on page
   */
  mediaOnPageLimit?: number;
  /**
   * Set the limit of an element on page
   */
  setMediaOnPageLimit?: ResultsPerPageChangeFn;
  /**
   *   Test id for unsplash media subpage
   */
  testId?: string;
  /**
   *   CSS classes for Media grid container
   */
  additionalGridContainerClasses?: string;
  /**
   *  Callback to reload media space used
   */
  handleReloadMedia?: () => Promise<void> | void;
}

const UnsplashMedia = ({
  mediaOnPageLimit = 8,
  setMediaOnPageLimit = EMPTY_FUNCTION,
  additionalGridContainerClasses = '',
  handleReloadMedia = EMPTY_FUNCTION,
  testId = '',
}: UnsplashMediaProps) => {
  const [page, setPage] = useState(1);
  const [tag, setTag] = useState('');
  const [orientation, setOrientation] = useState('');
  const [color, setColor] = useState('');
  const jwt = useToken();
  const { space } = useSelectedSpace();
  const { t } = useTranslation();

  const handleTagFilters = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      setTag(event.target.value);
      setPage(1);
    },
    [],
  );

  const orientationOptions = useMemo(() => {
    const options = [
      {
        value: 'landscape',
        label: t('Media.Filters.OrientationOptions.Landscape'),
      },
      {
        value: 'portrait',
        label: t('Media.Filters.OrientationOptions.Portrait'),
      },
      {
        value: 'squarish',
        label: t('Media.Filters.OrientationOptions.Squarish'),
      },
    ];
    return options.map((option) => ({
      value: option.value,
      searchString: option.label,
      label: option.label,
    }));
  }, [t]);

  const handleOrientationFilters = useCallback((value: string) => {
    setOrientation(value);
    setPage(1);
  }, []);

  const colorOptions = useMemo(() => {
    const options = [
      {
        value: 'black_and_white',
        label: t('Media.Filters.ColorOptions.BlackAndWhite'),
      },
      {
        value: 'black',
        label: t('Media.Filters.ColorOptions.Black'),
      },
      {
        value: 'white',
        label: t('Media.Filters.ColorOptions.White'),
      },
      {
        value: 'yellow',
        label: t('Media.Filters.ColorOptions.Yellow'),
      },
      {
        value: 'orange',
        label: t('Media.Filters.ColorOptions.Orange'),
      },
      {
        value: 'red',
        label: t('Media.Filters.ColorOptions.Red'),
      },
      {
        value: 'purple',
        label: t('Media.Filters.ColorOptions.Purple'),
      },
      {
        value: 'magenta',
        label: t('Media.Filters.ColorOptions.Magenta'),
      },
      {
        value: 'green',
        label: t('Media.Filters.ColorOptions.Green'),
      },
      {
        value: 'teal',
        label: t('Media.Filters.ColorOptions.Teal'),
      },
      {
        value: 'blue',
        label: t('Media.Filters.ColorOptions.Blue'),
      },
    ];
    return options.map((option) => ({
      value: option.value,
      searchString: option.label,
      label: option.label,
    }));
  }, [t]);

  const handleColorFilters = useCallback((value: string) => {
    setColor(value);
    setPage(1);
  }, []);

  const UnsplashPlusImageError = useMemo(() => 'unsplash-plus-image', []);

  const params = useMemo(() => {
    return {
      limit: mediaOnPageLimit,
      clientOriginalName: tag,
      page: page,
      orientation: orientation,
      color: color,
    };
  }, [page, mediaOnPageLimit, tag, orientation, color]);

  const { data, errors, isLoading, pagination } = useUnsplashMedia(params);

  useApiErrorsToast(errors);

  const handlePageChange = useCallback<PageChangeFn>((pageNo) => {
    setPage(pageNo);
  }, []);

  const saveMedia = useCallback(
    async (unsplashMediaId: string) => {
      try {
        newMediaStore.trigger.updateNewMedia({
          newMedia: [{ media: { id: unsplashMediaId }, state: 'uploading' }],
        });
        const { body, status, ok } = await saveUnsplashMedia(jwt, space, {
          mediaId: unsplashMediaId,
        });
        if (handleReloadMedia) {
          handleReloadMedia();
        }
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(t('Media.OnSaveSuccess'));

        newMediaStore.trigger.updateNewMedia({
          newMedia: [{ media: body, state: 'unsplash' }],
        });
        return true;
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
        } else {
          if (error.message === UnsplashPlusImageError) {
            toast.error(t('Media.UnsplashPlusMediaError'));
            return false;
          }

          toast.error(
            error.message ? error.message : t('Media.OnErrorMessage'),
          );
        }
        return false;
      }
    },
    [jwt, space, handleReloadMedia, t, UnsplashPlusImageError],
  );

  const filtersNode = useMemo(() => {
    return (
      <div className={'md:grid md:gap-6 md:grid-cols-3 lg:grid-cols-4'}>
        <Input
          testId={testId}
          label={t('Media.Filters.SearchByTag')}
          placeholder={t('Media.Filters.SearchByTag')}
          onChange={handleTagFilters}
          value={tag}
          type="search"
        />
        <Dropdown
          testId={`${testId}-orientation`}
          label={t('Media.Filters.SearchByOrientation')}
          placeholder={t('Media.Filters.SearchByOrientation')}
          options={orientationOptions}
          onChange={(event) => {
            handleOrientationFilters(event.target.value);
          }}
          value={orientation}
          additionalOptionsClasses="py-1.5"
          nullable
          {...getTestProps(testId, 'orientation', 'testId')}
        />
        <Dropdown
          testId={`${testId}-color`}
          label={t('Media.Filters.SearchByColor')}
          placeholder={t('Media.Filters.SearchByColor')}
          options={colorOptions}
          onChange={(event) => {
            handleColorFilters(event.target.value);
          }}
          value={color}
          additionalOptionsClasses="py-1.5"
          nullable
          {...getTestProps(testId, 'color', 'testId')}
        />
      </div>
    );
  }, [
    testId,
    t,
    handleTagFilters,
    tag,
    orientationOptions,
    orientation,
    colorOptions,
    color,
    handleOrientationFilters,
    handleColorFilters,
  ]);

  const renderMedia = useCallback(
    (media: Media, isNew?: boolean, index?: number) => (
      <UnsplashCard
        key={`${media.id}-${index}`}
        mediaData={media}
        saveMediaCallback={saveMedia}
        testId={testId}
      />
    ),
    [saveMedia, testId],
  );

  return (
    <>
      <MediaGrid
        page={page}
        data={data}
        renderMedia={renderMedia}
        isLoading={isLoading}
        errors={errors}
        pagination={pagination}
        handlePageChange={handlePageChange}
        areFiltersApplied={Object.keys(tag).length !== 0}
        filtersNode={filtersNode}
        handleNoData={
          <div className="flex justify-center">
            <Heading level={2} color="blue">
              {t('Media.UnsplashNotFound')}
            </Heading>
          </div>
        }
        hasPanel={false}
        emptyFilterText={t('Media.FiltersEmptyResult')}
        testId={testId}
        resultsPerPage={mediaOnPageLimit}
        setResultsPerPage={setMediaOnPageLimit}
        additionalGridContainerClasses={twMerge(
          'px-5',
          additionalGridContainerClasses,
        )}
      />
    </>
  );
};

export default UnsplashMedia;
